import { useEffect, useState } from "react";

interface Butterfly {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
  isLanding: boolean;
}

const BUTTERFLY_COLORS = [
  "#F472B6", // pink
  "#A78BFA", // purple
  "#60A5FA", // blue
  "#FBBF24", // yellow
  "#FB7185", // rose
  "#FB923C", // orange
];

const ButterflyAnimation = () => {
  const [butterflies, setButterflies] = useState<Butterfly[]>([]);

  useEffect(() => {
    // Create 5-7 butterflies with random properties
    const count = Math.floor(Math.random() * 3) + 5;
    const newButterflies: Butterfly[] = [];

    for (let i = 0; i < count; i++) {
      newButterflies.push({
        id: i,
        x: Math.random() * 80 + 10, // 10-90% of screen
        y: Math.random() * 60 + 10, // 10-70% of screen
        size: Math.random() * 24 + 36, // 36-60px (tripled from 12-20px)
        color: BUTTERFLY_COLORS[Math.floor(Math.random() * BUTTERFLY_COLORS.length)],
        delay: Math.random() * 3,
        duration: Math.random() * 4 + 6, // 6-10s
        isLanding: i === 0, // First butterfly will land on the box
      });
    }

    setButterflies(newButterflies);
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
      {butterflies.map((butterfly) => (
        <div
          key={butterfly.id}
          className="absolute opacity-90"
          style={{
            left: `${butterfly.x}%`,
            top: `${butterfly.y}%`,
            animationDelay: `${butterfly.delay}s`,
            animationDuration: `${butterfly.duration}s`,
          }}
        >
          <svg
            width={butterfly.size}
            height={butterfly.size}
            viewBox="0 0 100 100"
            className={butterfly.isLanding ? "animate-butterfly-land" : "animate-butterfly-fly"}
            style={{
              animationDelay: `${butterfly.delay}s`,
              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
            }}
          >
            {/* Realistic butterfly shape */}
            <g transform="translate(50, 50)">
              {/* Left wing - upper */}
              <ellipse
                cx="-18"
                cy="-12"
                rx="22"
                ry="18"
                fill={butterfly.color}
                opacity="0.9"
                className="animate-butterfly-wings"
                style={{ transformOrigin: "0 0", animationDelay: `${butterfly.delay}s` }}
              />
              {/* Left wing - lower */}
              <ellipse
                cx="-14"
                cy="10"
                rx="16"
                ry="14"
                fill={butterfly.color}
                opacity="0.85"
                className="animate-butterfly-wings"
                style={{ transformOrigin: "0 0", animationDelay: `${butterfly.delay}s` }}
              />
              {/* Right wing - upper */}
              <ellipse
                cx="18"
                cy="-12"
                rx="22"
                ry="18"
                fill={butterfly.color}
                opacity="0.9"
                className="animate-butterfly-wings"
                style={{ transformOrigin: "0 0", animationDelay: `${butterfly.delay + 0.05}s` }}
              />
              {/* Right wing - lower */}
              <ellipse
                cx="14"
                cy="10"
                rx="16"
                ry="14"
                fill={butterfly.color}
                opacity="0.85"
                className="animate-butterfly-wings"
                style={{ transformOrigin: "0 0", animationDelay: `${butterfly.delay + 0.05}s` }}
              />
              {/* Wing patterns - left */}
              <circle cx="-20" cy="-14" r="5" fill="white" opacity="0.4" />
              <circle cx="-15" cy="-8" r="3" fill="white" opacity="0.3" />
              <circle cx="-16" cy="8" r="4" fill="white" opacity="0.35" />
              {/* Wing patterns - right */}
              <circle cx="20" cy="-14" r="5" fill="white" opacity="0.4" />
              <circle cx="15" cy="-8" r="3" fill="white" opacity="0.3" />
              <circle cx="16" cy="8" r="4" fill="white" opacity="0.35" />
              {/* Body */}
              <ellipse cx="0" cy="0" rx="3" ry="20" fill="#1a1a1a" />
              {/* Head */}
              <circle cx="0" cy="-22" r="4" fill="#1a1a1a" />
              {/* Antennae */}
              <path
                d="M-2 -26 Q-6 -34 -8 -36"
                stroke="#1a1a1a"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M2 -26 Q6 -34 8 -36"
                stroke="#1a1a1a"
                strokeWidth="1.5"
                fill="none"
              />
              <circle cx="-8" cy="-36" r="1.5" fill="#1a1a1a" />
              <circle cx="8" cy="-36" r="1.5" fill="#1a1a1a" />
            </g>
          </svg>
        </div>
      ))}
    </div>
  );
};

export default ButterflyAnimation;
